import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export default function HotelCheckout() {
  const loc = useLocation() as any;
  const offer = loc?.state?.offer;
  const tenor = loc?.state?.tenor || 12;
  const total = Number(offer?.total_amount || offer?.total || 0);
  const [guest, setGuest] = useState({ given_name: "", family_name: "", email: "" });
  const [loading, setLoading] = useState(false);

  if (!offer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-6">
          <h2 className="text-xl font-semibold text-foreground mb-2">No Hotel Selected</h2>
          <p className="text-muted-foreground">Please go back and select a hotel.</p>
        </div>
      </div>
    );
  }

  const monthly = Math.ceil(total / tenor);
  const hotel = offer?.accommodation || offer?.hotel || {};
  const room = offer?.room_rate || offer?.rate || {};

  async function payWithTabby() {
    // Validate guest information
    if (!guest.given_name.trim() || !guest.family_name.trim() || !guest.email.trim()) {
      alert("Please fill in all guest information.");
      return;
    }

    setLoading(true);
    
    const hotelName = hotel?.name || "Hotel booking";
    const location = hotel?.location?.city || hotel?.address || "";
    const title = location ? `${hotelName} - ${location}` : hotelName;
    
    const payload = {
      amount: total,
      currency: "AED",
      order_reference: offer.id,
      buyer: {
        email: guest.email.trim(),
        phone: "+971500000000",
        name: `${guest.given_name.trim()} ${guest.family_name.trim()}`
      },
      metadata: {
        title: title,
        description: "Hotel booking via Flexaura",
        product_type: "stays",
        guests: [{ 
          given_name: guest.given_name.trim(), 
          family_name: guest.family_name.trim(),
          email: guest.email.trim()
        }]
      },
      lang: "en"
    };

    try {
      const { data, error } = await supabase.functions.invoke('pay-bnpl', {
        body: payload
      });
      
      setLoading(false);

      if (error) {
        console.error('Payment error:', error);
        alert("Unable to start BNPL checkout. Please try again.");
        return;
      }

      // Handle different response formats from BNPL providers
      if (data?.configuration?.available_products?.length && data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert("Unable to start BNPL checkout.");
      }
    } catch (error) {
      setLoading(false);
      console.error('Payment error:', error);
      alert("Unable to start BNPL checkout. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-heading font-semibold mb-4 text-flexaura-navy">
          Hotel Checkout
        </h2>
        
        <div className="border border-border rounded-2xl p-5 card-shadow bg-card">
          {/* Hotel Summary */}
          <div className="mb-6 p-4 bg-muted/50 rounded-lg">
            <div className="text-lg font-medium text-flexaura-navy mb-1">
              {hotel?.name || "Hotel Booking"}
            </div>
            <div className="text-sm text-muted-foreground mb-2">
              {hotel?.location?.city || hotel?.address || "Location"}
            </div>
            {room?.room_type && (
              <div className="text-sm text-foreground mb-2">
                Room: {room.room_type}
              </div>
            )}
            <div className="text-lg font-medium text-flexaura-navy mb-1">
              Total: AED {total.toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">
              From AED {monthly}/mo × {tenor} months
            </div>
          </div>

          {/* Guest Information Form */}
          <div className="space-y-4 mb-6">
            <div className="font-medium text-flexaura-navy">Lead Guest Information</div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input 
                  className="rounded-2xl border border-input bg-background px-3 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 smooth-transition" 
                  placeholder="First name *" 
                  value={guest.given_name}
                  onChange={e => setGuest(prev => ({ ...prev, given_name: e.target.value }))}
                />
                <input 
                  className="rounded-2xl border border-input bg-background px-3 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 smooth-transition" 
                  placeholder="Last name *" 
                  value={guest.family_name}
                  onChange={e => setGuest(prev => ({ ...prev, family_name: e.target.value }))}
                />
              </div>
              <input 
                type="email"
                className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 smooth-transition" 
                placeholder="Email address *" 
                value={guest.email}
                onChange={e => setGuest(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
          </div>

          {/* Payment Options */}
          <div className="space-y-3">
            <div className="font-medium text-flexaura-navy mb-3">Payment Method</div>
            
            <button 
              disabled={loading}
              onClick={payWithTabby}
              className="w-full px-6 py-4 rounded-2xl text-white font-medium gradient-bg hover:opacity-90 smooth-transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  Starting...
                </>
              ) : (
                "Pay monthly with Tabby"
              )}
            </button>
            
            <button 
              disabled 
              className="w-full px-6 py-4 rounded-2xl border border-border font-medium text-muted-foreground bg-muted/50 cursor-not-allowed"
            >
              Pay in full (card) — coming soon
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-6 p-4 bg-muted/30 rounded-lg">
            <div className="text-sm text-muted-foreground">
              <p className="mb-2">
                💡 <strong>Pay monthly:</strong> Split your payment into {tenor} easy monthly installments with Tabby.
              </p>
              <p className="mb-2">
                🏨 <strong>Instant confirmation:</strong> Your booking will be confirmed immediately after payment.
              </p>
              <p>
                🔒 Your payment is secured and processed by our trusted payment partners.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}